import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { getAbbrState } from './helpers/helpers.js';

if (typeof window.kw_from_search === 'undefined') window.kw_from_search = '';
if (typeof window.predefined_kw === 'undefined') window.predefined_kw = '';
if (typeof window.predefined_cats === 'undefined') window.predefined_cats = [];
if (typeof window.predefined_job_types === 'undefined') window.predefined_job_types = [];
if (typeof window.predefined_state === 'undefined') window.predefined_state = '';
if (typeof window.predefined_city === 'undefined') window.predefined_city = '';

const useFetchData = (jobParams = {}) => {
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);

    const queryParams = (userKeywords, predefinedKeywords, filters) => {
        let query = { bool: { must: [] } },
            facet_filter = [
                {
                    bool: {
                        must: [{ term: { 'job_board_url.enum': 'http://www.rphonthego.com' } }],
                    },
                },
            ];

        const field_to_use = ["job_title^3", "job_city^2", "job_description_raw", "job_category", "job_type"];
        let sort = [{ 'modified_at.date': 'desc' }]; // Default sort

        // Add a multi_match clause for predefined keywords if they exist
        if (predefinedKeywords && predefinedKeywords.trim() !== "") {
            query.bool.must.push({
                "multi_match": {
                    "query": predefinedKeywords.trim(),
                    "fields": field_to_use,
                    "operator": "or"
                }
            });
        }

        // Add a multi_match clause for user-entered keywords if they exist
        if (userKeywords && userKeywords.trim() !== "") {
            const re = /\S+@\S+\.\S+/;
            const userFields = (re.test(userKeywords)) ? ["org_unit_email"] : field_to_use;
            
            query.bool.must.push({
                "multi_match": {
                    "query": userKeywords.trim(),
                    "fields": userFields,
                    "operator": "and"
                }
            });
            
            if (!re.test(userKeywords)) {
                sort = [{ "_score": { "order": "desc" } }, { "modified_at.date": { "order": "desc", "format": "strict_date_optional_time_nanos" } }];
            } else {
                 sort = [{ "modified_at.date": { "order": "desc", "format": "strict_date_optional_time_nanos" } }];
            }
        }
        
        // Add all your facet filters to the 'filter' clause of the main query
        if (filters && filters.states && filters.states.length > 0) {
            facet_filter[0]['bool']['must'].push({ terms: { 'job_state_abbr.enum': filters.states } });
        } else if (window.predefined_state !== '') {
            facet_filter[0]['bool']['must'].push({
                terms: { 'job_state_abbr.enum': [getAbbrState(window.predefined_state)] },
            });
        }
    
        if (filters && filters.cities && filters.cities.length > 0) {
            facet_filter.push({ terms: { 'job_city.enum': filters.cities } });
        } else if (window.predefined_city !== '') {
            facet_filter.push({ terms: { 'job_city.enum': [window.predefined_city] } });
        }
    
        if (filters && filters.categories && filters.categories.length > 0) {
            facet_filter.push({ terms: { 'job_category.enum': filters.categories } });
        } else if (window.predefined_cats.length > 0) {
            facet_filter[0]['bool']['must'].push({
                terms: { 'job_category_id.float': JSON.parse(window.predefined_cats) },
            });
        } else if (window.predefined_job_types.length > 0) {
            facet_filter[0]['bool']['must'].push({
                terms: { 'job_type_id.float': JSON.parse(window.predefined_job_types) },
            });
        }
    
        facet_filter[0]['bool']['must'].push({ range: { 'modified_at.date': { gte: 'now-61d/d' } } });
        
        query.bool.filter = facet_filter;
       // console.log("THE QUERY: " + JSON.stringify(query));

        return { query, sort };
    };

    useEffect(() => {
        const source = axios.CancelToken.source();

        const userKeywords = jobParams.hasOwnProperty('keywords') ? jobParams['keywords'] : window.kw_from_search;
        const predefinedKeywords = window.predefined_kw;
        
        const sort_id = jobParams.hasOwnProperty('sort_id') ? jobParams['sort_id'] : null;
        const sort_id_qs = sort_id !== null && `sort_id=${sort_id}`;
        const search_after_count = jobParams.hasOwnProperty('search_after_count')
            ? jobParams['search_after_count']
            : null;
        const search_after_count_qs = search_after_count !== null && `search_after_count=${search_after_count}`;
        const from = jobParams.hasOwnProperty('from') ? jobParams['from'] : 0;
        const qs_params = [sort_id_qs, search_after_count_qs].filter(Boolean).join('&');
        
        const { query, sort } = queryParams(userKeywords, predefinedKeywords, jobParams);
        
        const compiledData = {
            from: from,
            size: 1000,
            query: query,
            aggs: {
                job_count: {
                    value_count: { field: 'job_board_url.enum' },
                },
                categorieslist: {
                    terms: {
                        field: 'job_category.enum',
                        size: 200,
                        order: { _key: 'asc' },
                    },
                },
                stateslist: {
                    terms: {
                        field: 'job_state_abbr.enum',
                        size: 51,
                        order: { _key: 'asc' },
                    },
                    aggs: {
                        'city-list': {
                            terms: {
                                field: 'job_city.enum',
                                size: 15000,
                                order: { _key: 'asc' },
                            },
                        },
                    },
                },
                "typelist": {
                    "terms": {
                        "field": "job_type.enum",
                        "size": 10,
                        "order": {"_key" : "asc"}
                    }
                },
            },
            fields: [
                'job_order_id',
                'job_title',
                'job_category_main',
                'job_category',
                'job_category_id',
                'job_type',
                'job_city',
                'job_state_abbr',
                'job_postal',
                "job_description",
                "job_description_raw",
                'start_date',
                'end_date',
                'modified_at',
            ],
            sort: sort,
            _source: false,
        };
       // console.log("compiledJSONData: " + JSON.stringify(compiledData));

        const fetchData = async () => {
            try {
                setLoading(true);
                const { data: response } = await axios({
                    method: 'post',
                    url: 'https://proxy.sunbeltstaffingjobs.com/post_job_search.php?' + qs_params,
                    cancelToken: source.token,
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'Token',
                        'Access-Control-Allow-Origin': '*',
                    },
                    data: JSON.stringify(compiledData),
                });
                setData(response);
            } catch (error) {
                console.error('There was an error: ' + error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();

        return () => {
            if (source) source.cancel();
        };
    }, [jobParams]);

    return { data, loading };
};

export default useFetchData;